import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Plus,
    Trash2,
    Save,
    ChevronRight,
    AlertTriangle,
    Filter,
    RefreshCw,
    X,
} from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";

const STAGE_OPTIONS = ["Triage", "Universal", "Branch", "Red Flag"];
const CATEGORY_OPTIONS = [
    "General Wellness",
    "Digestive Health",
    "Sleep & Stress",
    "Skin & Hair",
    "Weight & Metabolism",
    "Immunity",
    "Joint Pain",
    "Red Flag",
    "Other",
];

function emptyQuestion() {
    return {
        quiz_id: "",
        question_text: "",
        question_type: "single_select",
        health_category: "General Wellness",
        stage: "Universal",
        parent_quiz_id: null,
        trigger_answer: null,
        required: true,
        red_flag_screen: false,
        order: 1000,
        active: true,
        answers: [],
    };
}

function newAnswer(idx) {
    return {
        answer_id: `A${Date.now().toString().slice(-6)}${idx}`,
        answer_text: "",
        symptom_id: "",
        score: 0,
    };
}

function EditQuestionModal({ initial, onClose, onSaved }) {
    const [q, setQ] = useState(initial);
    const [saving, setSaving] = useState(false);

    function setField(k, v) {
        setQ((prev) => ({ ...prev, [k]: v }));
    }

    function updateAnswer(idx, patch) {
        setQ((prev) => ({
            ...prev,
            answers: prev.answers.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
        }));
    }

    function addAnswer() {
        setQ((prev) => ({
            ...prev,
            answers: [...(prev.answers || []), newAnswer(prev.answers?.length || 0)],
        }));
    }

    function removeAnswer(idx) {
        setQ((prev) => ({
            ...prev,
            answers: prev.answers.filter((_, i) => i !== idx),
        }));
    }

    async function save() {
        if (!q.quiz_id.trim() || !q.question_text.trim()) {
            toast.error("quiz_id and question text are required.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...q,
                parent_quiz_id: q.parent_quiz_id || null,
                trigger_answer: q.trigger_answer || null,
                order: Number(q.order) || 0,
                answers: (q.answers || []).map((a) => ({
                    answer_id: a.answer_id,
                    answer_text: a.answer_text,
                    symptom_id: a.symptom_id?.trim() || null,
                    score: Number(a.score) || 0,
                })),
            };
            const { data } = await api.post("/admin/quiz/questions", payload);
            toast.success("Saved.");
            onSaved(data);
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not save.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-40 bg-black/40 p-4">
            <div className="mx-auto mt-8 max-h-[92vh] max-w-3xl overflow-y-auto border border-veda-border bg-veda-surface p-5 md:p-8">
                <div className="flex items-baseline justify-between gap-4">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                            Quiz question
                        </p>
                        <h2 className="font-display mt-1 text-2xl text-veda-onSurface">
                            {initial.quiz_id ? "Edit" : "New"} question
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-veda-muted hover:text-veda-onSurface"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="mt-5 grid gap-3 md:grid-cols-2">
                    <Field label="quiz_id" hint="Unique, e.g. Q011">
                        <input
                            type="text"
                            value={q.quiz_id}
                            onChange={(e) => setField("quiz_id", e.target.value.trim())}
                            disabled={!!initial.quiz_id}
                            className="w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm disabled:opacity-70"
                        />
                    </Field>
                    <Field label="Order">
                        <input
                            type="number"
                            value={q.order}
                            onChange={(e) => setField("order", e.target.value)}
                            className="w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm"
                        />
                    </Field>
                    <Field label="Question text" full>
                        <input
                            type="text"
                            value={q.question_text}
                            onChange={(e) => setField("question_text", e.target.value)}
                            className="w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm"
                        />
                    </Field>
                    <Field label="Type">
                        <select
                            value={q.question_type}
                            onChange={(e) => setField("question_type", e.target.value)}
                            className="w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm"
                        >
                            <option value="single_select">single_select</option>
                            <option value="yes_no">yes_no</option>
                        </select>
                    </Field>
                    <Field label="Stage">
                        <select
                            value={q.stage}
                            onChange={(e) => setField("stage", e.target.value)}
                            className="w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm"
                        >
                            {STAGE_OPTIONS.map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Health category">
                        <select
                            value={q.health_category}
                            onChange={(e) => setField("health_category", e.target.value)}
                            className="w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm"
                        >
                            {CATEGORY_OPTIONS.map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Parent quiz_id (branch)" hint="Leave empty for universal">
                        <input
                            type="text"
                            value={q.parent_quiz_id || ""}
                            onChange={(e) => setField("parent_quiz_id", e.target.value.trim() || null)}
                            className="w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm"
                        />
                    </Field>
                    <Field label="Trigger answer" hint="The exact answer_text from parent">
                        <input
                            type="text"
                            value={q.trigger_answer || ""}
                            onChange={(e) => setField("trigger_answer", e.target.value || null)}
                            className="w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm"
                        />
                    </Field>
                    <label className="mt-2 inline-flex items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            checked={!!q.required}
                            onChange={(e) => setField("required", e.target.checked)}
                        />
                        Required
                    </label>
                    <label className="mt-2 inline-flex items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            checked={!!q.red_flag_screen}
                            onChange={(e) => setField("red_flag_screen", e.target.checked)}
                        />
                        Red flag screen
                    </label>
                    <label className="mt-2 inline-flex items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            checked={!!q.active}
                            onChange={(e) => setField("active", e.target.checked)}
                        />
                        Active
                    </label>
                </div>

                <div className="mt-6 border-t border-veda-divider pt-5">
                    <div className="flex items-baseline justify-between">
                        <p className="font-display text-lg text-veda-onSurface">
                            Answer options ({q.answers?.length || 0})
                        </p>
                        <button
                            type="button"
                            onClick={addAnswer}
                            className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface2 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface hover:border-veda-brand hover:text-veda-brand"
                        >
                            <Plus size={11} /> Add answer
                        </button>
                    </div>
                    <ul className="mt-3 space-y-2">
                        {(q.answers || []).map((a, i) => (
                            <li
                                key={a.answer_id}
                                className="grid grid-cols-12 gap-2 border border-veda-border bg-veda-surface2/50 p-2"
                            >
                                <input
                                    type="text"
                                    value={a.answer_id}
                                    onChange={(e) =>
                                        updateAnswer(i, { answer_id: e.target.value })
                                    }
                                    placeholder="answer_id"
                                    className="col-span-2 border border-veda-border bg-veda-surface px-2 py-1.5 text-xs"
                                />
                                <input
                                    type="text"
                                    value={a.answer_text}
                                    onChange={(e) =>
                                        updateAnswer(i, { answer_text: e.target.value })
                                    }
                                    placeholder="Answer text"
                                    className="col-span-5 border border-veda-border bg-veda-surface px-2 py-1.5 text-xs"
                                />
                                <input
                                    type="text"
                                    value={a.symptom_id || ""}
                                    onChange={(e) =>
                                        updateAnswer(i, { symptom_id: e.target.value })
                                    }
                                    placeholder="SYM_*"
                                    className="col-span-3 border border-veda-border bg-veda-surface px-2 py-1.5 text-xs"
                                />
                                <input
                                    type="number"
                                    value={a.score}
                                    onChange={(e) =>
                                        updateAnswer(i, { score: e.target.value })
                                    }
                                    placeholder="score"
                                    className="col-span-1 border border-veda-border bg-veda-surface px-2 py-1.5 text-xs"
                                />
                                <button
                                    type="button"
                                    onClick={() => removeAnswer(i)}
                                    className="col-span-1 grid place-items-center text-veda-muted hover:text-veda-error"
                                    aria-label="Delete answer"
                                >
                                    <Trash2 size={13} />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-veda-divider pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="border border-veda-border bg-veda-surface px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        className="inline-flex items-center gap-2 bg-veda-brand px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-white disabled:opacity-60"
                    >
                        <Save size={12} /> {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, full, children }) {
    return (
        <label className={`block ${full ? "md:col-span-2" : ""}`}>
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                {label}
            </span>
            <div className="mt-1.5">{children}</div>
            {hint && (
                <p className="mt-1 text-[10px] text-veda-muted">{hint}</p>
            )}
        </label>
    );
}

function SubmissionsPanel() {
    const [items, setItems] = useState([]);
    const [redFlagOnly, setRedFlagOnly] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 50 };
            if (redFlagOnly) params.red_flag = true;
            const { data } = await api.get("/admin/quiz/submissions", { params });
            setItems(data || []);
        } catch {
            toast.error("Could not load submissions.");
        } finally {
            setLoading(false);
        }
    }, [redFlagOnly]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <section data-testid={TID.adminQuizSubmissions} className="mt-8">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                        Quiz submissions
                    </p>
                    <h2 className="font-display mt-1 text-xl text-veda-onSurface">
                        {loading
                            ? "Loading…"
                            : items.length === 0
                            ? "No submissions yet"
                            : `${items.length} recent submission${items.length === 1 ? "" : "s"}`}
                    </h2>
                </div>
                <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            checked={redFlagOnly}
                            onChange={(e) => setRedFlagOnly(e.target.checked)}
                        />
                        Red flags only
                    </label>
                    <button
                        type="button"
                        onClick={load}
                        className="grid h-7 w-7 place-items-center border border-veda-border bg-veda-surface"
                        aria-label="Reload"
                    >
                        <RefreshCw size={12} />
                    </button>
                </div>
            </div>
            <div className="mt-3 overflow-x-auto border border-veda-border bg-veda-surface">
                <table className="w-full text-xs">
                    <thead className="bg-veda-surface2 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted">
                        <tr>
                            <th className="px-3 py-2 text-left">When</th>
                            <th className="px-3 py-2 text-left">User</th>
                            <th className="px-3 py-2 text-left">Prakriti</th>
                            <th className="px-3 py-2 text-left">Top concern</th>
                            <th className="px-3 py-2 text-left">Severity</th>
                            <th className="px-3 py-2 text-left">Flag</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-veda-divider">
                        {items.map((s) => (
                            <tr key={s.submission_id}>
                                <td className="px-3 py-2 text-veda-muted tabular-nums">
                                    {new Date(s.created_at).toLocaleString()}
                                </td>
                                <td className="px-3 py-2 text-veda-onSurface">
                                    {s.user
                                        ? `${s.user.name} · ${s.user.email}`
                                        : "Anonymous"}
                                </td>
                                <td className="px-3 py-2 capitalize">
                                    {s.computed?.prakriti?.primary || "—"}
                                </td>
                                <td className="px-3 py-2">
                                    {s.computed?.top_concern || "—"}
                                </td>
                                <td className="px-3 py-2 text-veda-muted">
                                    {s.computed?.severity || "—"}
                                </td>
                                <td className="px-3 py-2">
                                    {s.computed?.red_flag ? (
                                        <span className="inline-flex items-center gap-1 rounded-pill bg-veda-error px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white">
                                            <AlertTriangle size={10} /> Red
                                        </span>
                                    ) : (
                                        <span className="text-veda-muted">—</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && !loading && (
                            <tr>
                                <td
                                    colSpan={6}
                                    className="px-3 py-6 text-center text-veda-muted"
                                >
                                    Nothing here yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

export default function AdminQuiz() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showActiveOnly, setShowActiveOnly] = useState(false);
    const [editing, setEditing] = useState(null); // question object or null

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/quiz/questions");
            setItems(data || []);
        } catch {
            toast.error("Could not load quiz.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(
        () => (showActiveOnly ? items.filter((i) => i.active) : items),
        [items, showActiveOnly],
    );

    async function toggleActive(q) {
        try {
            await api.patch(`/admin/quiz/questions/${q.quiz_id}`, {
                active: !q.active,
            });
            load();
        } catch {
            toast.error("Could not toggle.");
        }
    }

    async function del(q) {
        if (!window.confirm(`Delete ${q.quiz_id}? Existing submissions keep their history.`))
            return;
        try {
            await api.delete(`/admin/quiz/questions/${q.quiz_id}`);
            toast.success("Deleted.");
            load();
        } catch {
            toast.error("Could not delete.");
        }
    }

    return (
        <div data-testid={TID.adminQuizPage} className="pb-16">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-brand">
                        Onboarding quiz
                    </p>
                    <h2 className="font-display mt-1 text-2xl text-veda-onSurface md:text-3xl">
                        Questions & answers
                    </h2>
                    <p className="mt-1 text-sm text-veda-muted">
                        Edit prompts, add options, tweak branching, or hide a
                        question without redeploying.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-2 text-xs">
                        <Filter size={12} className="text-veda-muted" />
                        <input
                            type="checkbox"
                            checked={showActiveOnly}
                            onChange={(e) => setShowActiveOnly(e.target.checked)}
                        />
                        Active only
                    </label>
                    <button
                        type="button"
                        onClick={() => setEditing(emptyQuestion())}
                        data-testid={TID.adminQuizAdd}
                        className="inline-flex items-center gap-2 bg-veda-brand px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-white"
                    >
                        <Plus size={12} /> New question
                    </button>
                </div>
            </div>

            <div className="mt-6 overflow-x-auto border border-veda-border bg-veda-surface">
                <table className="w-full text-xs">
                    <thead className="bg-veda-surface2 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted">
                        <tr>
                            <th className="px-3 py-2 text-left">ID</th>
                            <th className="px-3 py-2 text-left">Question</th>
                            <th className="px-3 py-2 text-left">Category</th>
                            <th className="px-3 py-2 text-left">Stage</th>
                            <th className="px-3 py-2 text-left">Branch</th>
                            <th className="px-3 py-2 text-left">Options</th>
                            <th className="px-3 py-2 text-left">Active</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-veda-divider">
                        {filtered.map((q) => (
                            <tr
                                key={q.quiz_id}
                                data-testid={TID.adminQuizItem(q.quiz_id)}
                                className={q.active ? "" : "opacity-60"}
                            >
                                <td className="px-3 py-2 font-semibold text-veda-onSurface">
                                    {q.quiz_id}
                                </td>
                                <td className="px-3 py-2 max-w-md">
                                    <p className="truncate text-veda-onSurface">
                                        {q.question_text}
                                    </p>
                                    {q.red_flag_screen && (
                                        <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-veda-error">
                                            <AlertTriangle size={9} /> red flag
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-veda-muted">
                                    {q.health_category}
                                </td>
                                <td className="px-3 py-2 text-veda-muted">
                                    {q.stage}
                                </td>
                                <td className="px-3 py-2 text-veda-muted">
                                    {q.parent_quiz_id ? (
                                        <span>
                                            {q.parent_quiz_id} ={" "}
                                            <em>{q.trigger_answer}</em>
                                        </span>
                                    ) : (
                                        <span>—</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-veda-muted">
                                    {q.answers?.length || 0}
                                </td>
                                <td className="px-3 py-2">
                                    <button
                                        type="button"
                                        onClick={() => toggleActive(q)}
                                        data-testid={TID.adminQuizToggleActive(q.quiz_id)}
                                        className={`inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                                            q.active
                                                ? "border border-veda-brand bg-veda-brand text-white"
                                                : "border border-veda-border bg-veda-surface2 text-veda-muted"
                                        }`}
                                    >
                                        {q.active ? "on" : "off"}
                                    </button>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex items-center justify-end gap-1">
                                        <button
                                            type="button"
                                            onClick={() => setEditing(q)}
                                            data-testid={TID.adminQuizEdit(q.quiz_id)}
                                            className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-veda-onSurface hover:border-veda-brand hover:text-veda-brand"
                                        >
                                            Edit <ChevronRight size={10} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => del(q)}
                                            data-testid={TID.adminQuizDelete(q.quiz_id)}
                                            className="grid h-7 w-7 place-items-center text-veda-muted hover:text-veda-error"
                                            aria-label="Delete"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {loading && (
                            <tr>
                                <td
                                    colSpan={8}
                                    className="px-3 py-6 text-center text-veda-muted"
                                >
                                    Loading…
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <SubmissionsPanel />

            {editing && (
                <EditQuestionModal
                    initial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        load();
                    }}
                />
            )}
        </div>
    );
}
